<template>
  <div
    :class="
      embedded
        ? 'flex flex-col flex-1 overflow-hidden font-sans'
        : 'flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm font-sans'
    "
  >
    <!-- ── Header (standalone only) ── -->
    <div
      v-if="!embedded"
      class="flex-shrink-0 px-4 py-3 bg-gradient-to-br from-teal-600 to-purple-500 text-white flex justify-between items-center"
    >
      <div class="flex items-center gap-2.5">
        <div
          class="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <div>
          <h3 class="m-0 text-sm font-semibold leading-tight">
            {{ $t("ai.alerts.title") }}
          </h3>
          <span class="text-xs opacity-75 block">
            <template v-if="summary">{{
              $t("ai.alerts.activeAlertsCount", { count: summary.total })
            }}</template>
            <template v-else>{{ pbxHostName }}</template>
          </span>
        </div>
      </div>
      <div class="flex items-center gap-1.5">
        <button
          @click="checkNow"
          :disabled="checking"
          class="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white bg-opacity-10 border border-white border-opacity-20 text-white text-xs cursor-pointer transition-colors hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed"
          :title="$t('ai.alerts.checkNowTitle')"
        >
          <svg
            v-if="!checking"
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-.53-4.81" />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            class="animate-spin"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          {{ checking ? $t("ai.alerts.checking") : $t("ai.alerts.checkNow") }}
        </button>
        <button
          @click="showSettings = !showSettings"
          :class="
            showSettings
              ? 'bg-white bg-opacity-30 border-white border-opacity-30'
              : 'bg-white bg-opacity-10 border-white border-opacity-20 hover:bg-opacity-30'
          "
          class="w-8 h-8 rounded-lg border text-white cursor-pointer flex items-center justify-center transition-colors"
          :title="$t('ai.alerts.settings')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- ── Settings panel ── -->
    <div
      v-if="showSettings"
      class="flex-shrink-0 px-4 py-3 bg-slate-50 border-b border-gray-200"
    >
      <p
        class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2"
      >
        {{ $t("ai.alerts.settings") }}
      </p>
      <div class="grid grid-cols-2 gap-x-4 gap-y-2">
        <div class="flex items-center justify-between col-span-2">
          <label class="text-xs text-slate-600">{{
            $t("ai.alerts.alertsEnabled")
          }}</label>
          <button
            @click="
              settings.enabled = !settings.enabled;
              saveSettings();
            "
            :class="settings.enabled ? 'bg-teal-500' : 'bg-gray-300'"
            class="relative w-9 h-5 rounded-full transition-colors cursor-pointer border-0"
          >
            <span
              :class="settings.enabled ? 'translate-x-4' : 'translate-x-0.5'"
              class="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform block"
            ></span>
          </button>
        </div>
        <div class="flex items-center justify-between">
          <label class="text-xs text-slate-600">{{
            $t("ai.alerts.intervalMin")
          }}</label>
          <input
            type="number"
            v-model.number="settings.check_interval_minutes"
            min="15"
            max="1440"
            @change="saveSettings"
            class="w-16 px-2 py-1 text-xs border border-gray-200 rounded-md text-right focus:outline-none focus:border-teal-400"
          />
        </div>
        <div class="flex items-center justify-between">
          <label class="text-xs text-slate-600">Email</label>
          <input
            type="email"
            v-model="settings.notification_email"
            @change="saveSettings"
            placeholder="alerts@..."
            class="w-28 px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-teal-400"
          />
        </div>
      </div>
    </div>

    <!-- ── Summary stats ── -->
    <div
      v-if="summary || alerts.length"
      class="flex-shrink-0 grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100"
    >
      <div class="flex flex-col items-center py-2.5 px-2">
        <span
          class="text-lg font-bold"
          :class="warningCount > 0 ? 'text-yellow-500' : 'text-gray-300'"
          >{{ warningCount }}</span
        >
        <span class="text-xs text-gray-400 mt-0.5">{{
          $t("ai.alerts.warnings")
        }}</span>
      </div>
      <div class="flex flex-col items-center py-2.5 px-2">
        <span
          class="text-lg font-bold"
          :class="infoCount > 0 ? 'text-blue-500' : 'text-gray-300'"
          >{{ infoCount }}</span
        >
        <span class="text-xs text-gray-400 mt-0.5">{{
          $t("ai.alerts.infos")
        }}</span>
      </div>
      <div class="flex flex-col items-center py-2.5 px-2">
        <span class="text-lg font-bold text-gray-700">{{ alerts.length }}</span>
        <span class="text-xs text-gray-400 mt-0.5">{{
          $t("ai.alerts.total")
        }}</span>
      </div>
    </div>

    <!-- ── Category filter tabs ── -->
    <div
      v-if="alerts.length"
      class="flex-shrink-0 flex gap-1 px-3 pt-2.5 pb-1 overflow-x-auto"
    >
      <button
        v-for="tab in filterTabs"
        :key="tab.key"
        @click="activeFilter = tab.key"
        :class="
          activeFilter === tab.key
            ? 'bg-teal-50 text-teal-700 border-teal-200'
            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
        "
        class="flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium whitespace-nowrap cursor-pointer transition-colors flex-shrink-0"
      >
        <span>{{ tab.label }}</span>
        <span
          v-if="tab.count > 0"
          :class="
            activeFilter === tab.key
              ? 'bg-teal-100 text-teal-700'
              : 'bg-gray-200 text-gray-500'
          "
          class="text-xs font-bold rounded-full px-1.5 min-w-4 text-center leading-4"
          >{{ tab.count }}</span
        >
      </button>
    </div>

    <!-- ── Alert list ── -->
    <div
      class="flex-1 overflow-y-auto min-h-0"
      :style="embedded ? '' : 'max-height: 520px'"
    >
      <!-- Loading -->
      <div
        v-if="loading"
        class="flex justify-center items-center gap-1.5 py-10"
      >
        <span
          class="w-2 h-2 bg-teal-400 rounded-full animate-bounce"
          style="animation-delay: 0ms"
        ></span>
        <span
          class="w-2 h-2 bg-teal-400 rounded-full animate-bounce"
          style="animation-delay: 150ms"
        ></span>
        <span
          class="w-2 h-2 bg-teal-400 rounded-full animate-bounce"
          style="animation-delay: 300ms"
        ></span>
      </div>

      <!-- Empty -->
      <div
        v-else-if="!alerts.length"
        class="flex flex-col items-center justify-center py-12 text-center px-6"
      >
        <div
          class="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-3"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#16a34a"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <p class="text-sm font-medium text-gray-700 mb-1">
          {{ $t("ai.alerts.allClear") }}
        </p>
        <p class="text-xs text-gray-400">
          {{ $t("ai.alerts.noActiveAlerts") }}
        </p>
      </div>

      <!-- Alert cards -->
      <div v-else class="px-3 py-2 flex flex-col gap-2">
        <div
          v-for="alert in filteredAlerts"
          :key="alert.id"
          :class="severityCardClass(alert.severity)"
          class="rounded-xl border overflow-hidden"
        >
          <!-- Card header (always visible) -->
          <div
            class="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer select-none"
            @click="toggleExpanded(alert.id)"
          >
            <div class="flex-shrink-0 mt-0.5">
              <div
                :class="severityDotClass(alert.severity)"
                class="w-2 h-2 rounded-full mt-1"
              ></div>
            </div>

            <div
              :class="categoryIconClass(alert.category)"
              class="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
            >
              <svg
                v-if="alert.category === 'queue_performance'"
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.17h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.73a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
                />
              </svg>
              <svg
                v-else-if="alert.category === 'peak_hours'"
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <svg
                v-else-if="alert.category === 'volume'"
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>

            <div class="flex-1 min-w-0">
              <p
                class="text-xs font-semibold text-gray-800 leading-tight truncate"
              >
                {{ alert.title }}
              </p>
              <p class="text-xs text-gray-400 mt-0.5">
                {{ alert.affected_name || alert.affected_dn }}
              </p>
            </div>

            <div class="flex-shrink-0 flex flex-col items-end gap-0.5">
              <span
                v-if="alert.current_value !== null"
                :class="severityValueClass(alert.severity)"
                class="text-sm font-bold leading-none"
              >
                {{ formatMetricValue(alert) }}
              </span>
              <span
                v-if="alert.change_percent !== null"
                :class="
                  alert.change_percent > 0
                    ? 'text-red-500 bg-red-50'
                    : 'text-green-600 bg-green-50'
                "
                class="text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none"
              >
                {{ alert.change_percent > 0 ? "▲" : "▼"
                }}{{ Math.abs(alert.change_percent) }}%
              </span>
            </div>

            <div class="flex-shrink-0 ml-1">
              <svg
                :class="expandedAlerts.has(alert.id) ? 'rotate-180' : ''"
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                stroke-width="2.5"
                class="transition-transform"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>

          <!-- Expanded body -->
          <div
            v-if="expandedAlerts.has(alert.id)"
            class="px-3 pb-3 border-t"
            :class="severityBorderClass(alert.severity)"
          >
            <p class="text-xs text-gray-600 leading-relaxed pt-2.5">
              {{ alert.description }}
            </p>

            <div v-if="isRateMetric(alert)" class="mt-2.5 mb-1">
              <div class="flex justify-between text-xs text-gray-400 mb-1">
                <span>{{ formatMetricLabel(alert.metric) }}</span>
                <span>{{ alert.current_value }}%</span>
              </div>
              <div
                class="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden"
              >
                <div
                  :class="severityBarClass(alert.severity)"
                  class="h-full rounded-full transition-all"
                  :style="`width: ${Math.min(alert.current_value, 100)}%`"
                ></div>
              </div>
            </div>

            <div
              v-if="alert.recommendation"
              class="flex gap-2 mt-2.5 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#d97706"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="flex-shrink-0 mt-0.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p class="text-xs text-yellow-800 leading-relaxed">
                {{ alert.recommendation }}
              </p>
            </div>

            <div class="flex items-center justify-between mt-2.5">
              <span class="text-xs text-gray-400">{{
                formatTime(alert.created_at)
              }}</span>
              <button
                @click.stop="dismiss(alert.id)"
                class="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 cursor-pointer bg-gray-100 hover:bg-gray-200 border-0 rounded-md px-2 py-1 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                {{ $t("ai.alerts.dismiss") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Footer ── -->
    <div
      v-if="summary"
      class="flex-shrink-0 flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50"
    >
      <span class="text-xs text-gray-400"
        >{{ $t("ai.alerts.checked") }}
        {{ formatTime(summary.checked_at) }}</span
      >
      <span class="text-xs text-gray-400">{{
        $t("ai.alerts.autoRefresh")
      }}</span>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AiAlertPanel",
  emits: ["alert-count"],

  props: {
    pbxHostName: { type: String, required: true },
    apiBaseUrl: { type: String, required: true },
    bearerToken: { type: String, required: true },
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      alerts: [],
      summary: null,
      settings: {
        enabled: true,
        check_interval_minutes: 60,
        thresholds: {
          abandoned_rate_warning: 0.15,
          abandoned_rate_critical: 0.3,
          wait_time_warning: 60,
          wait_time_critical: 120,
          missed_calls_warning: 5,
          volume_change_percent: 50,
        },
        notification_email: null,
      },
      showSettings: false,
      loading: true,
      checking: false,
      activeFilter: "all",
      expandedAlerts: new Set(),
    };
  },

  computed: {
    headers() {
      return { Authorization: `Bearer ${this.bearerToken}` };
    },
    baseUrl() {
      return `${this.apiBaseUrl}/api/v1.2/${this.pbxHostName}/ai/alerts`;
    },
    warningCount() {
      return this.alerts.filter(
        (a) => a.severity === "warning" || a.severity === "critical",
      ).length;
    },
    infoCount() {
      return this.alerts.filter((a) => a.severity === "info").length;
    },
    filterTabs() {
      const categories = [
        { key: "all", label: this.$t("ai.alerts.filterAll") },
        { key: "queue_performance", label: this.$t("ai.alerts.filterQueues") },
        { key: "peak_hours", label: this.$t("ai.alerts.filterPeakHours") },
        { key: "volume", label: this.$t("ai.alerts.filterVolume") },
        { key: "agent_performance", label: this.$t("ai.alerts.filterAgents") },
      ];
      return categories
        .map((tab) => ({
          ...tab,
          count:
            tab.key === "all"
              ? this.alerts.length
              : this.alerts.filter((a) => a.category === tab.key).length,
        }))
        .filter((tab) => tab.key === "all" || tab.count > 0);
    },
    filteredAlerts() {
      if (this.activeFilter === "all") return this.alerts;
      return this.alerts.filter((a) => a.category === this.activeFilter);
    },
  },

  methods: {
    async fetchAlerts() {
      this.loading = true;
      try {
        const resp = await axios.get(this.baseUrl, { headers: this.headers });
        this.alerts = resp.data.alerts || [];
        this.summary = resp.data.summary || null;
        this.$emit("alert-count", this.alerts.length);
      } catch (e) {
        console.error("Failed to fetch alerts:", e);
      } finally {
        this.loading = false;
      }
    },

    async fetchSettings() {
      try {
        const resp = await axios.get(`${this.baseUrl}/settings`, {
          headers: this.headers,
        });
        Object.assign(this.settings, resp.data);
      } catch (e) {
        console.error("Failed to fetch settings:", e);
      }
    },

    async saveSettings() {
      try {
        await axios.post(`${this.baseUrl}/settings`, this.settings, {
          headers: this.headers,
        });
      } catch (e) {
        console.error("Failed to save settings:", e);
      }
    },

    async checkNow() {
      this.checking = true;
      try {
        await axios.post(
          `${this.baseUrl}/check`,
          {},
          { headers: this.headers, timeout: 120000 },
        );
        await this.fetchAlerts();
      } catch (e) {
        console.error("Alert check failed:", e);
      } finally {
        this.checking = false;
      }
    },

    async dismiss(alertId) {
      try {
        await axios.post(
          `${this.baseUrl}/${alertId}/dismiss`,
          {},
          { headers: this.headers },
        );
        this.alerts = this.alerts.filter((a) => a.id !== alertId);
        this.expandedAlerts.delete(alertId);
        if (this.summary) this.summary.total--;
      } catch (e) {
        console.error("Dismiss failed:", e);
      }
    },

    toggleExpanded(id) {
      const next = new Set(this.expandedAlerts);
      next.has(id) ? next.delete(id) : next.add(id);
      this.expandedAlerts = next;
    },

    isRateMetric(alert) {
      return (
        alert.metric?.includes("rate") || alert.metric?.includes("percent")
      );
    },

    formatMetricValue(alert) {
      if (alert.metric?.includes("rate") || alert.metric === "volume_change") {
        return `${alert.current_value}%`;
      }
      return alert.current_value;
    },

    formatMetricLabel(metric) {
      const map = {
        not_answered_rate: "ai.alerts.metricNotAnsweredRate",
        abandon_rate: "ai.alerts.metricAbandonRate",
        volume_change: "ai.alerts.metricVolumeChange",
        agent_calls: "ai.alerts.metricAgentCalls",
      };
      return map[metric] ? this.$t(map[metric]) : metric;
    },

    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      const now = new Date();
      const diff = (now - d) / 1000;
      if (diff < 3600) return `${Math.floor(diff / 60)}m`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      return d.toLocaleDateString();
    },

    severityCardClass(severity) {
      if (severity === "critical") return "border-red-200 bg-red-50/40";
      if (severity === "warning") return "border-yellow-200 bg-yellow-50/30";
      return "border-blue-100 bg-blue-50/20";
    },
    severityBorderClass(severity) {
      if (severity === "critical") return "border-red-100";
      if (severity === "warning") return "border-yellow-100";
      return "border-blue-100";
    },
    severityDotClass(severity) {
      if (severity === "critical") return "bg-red-500";
      if (severity === "warning") return "bg-yellow-400";
      return "bg-blue-400";
    },
    severityValueClass(severity) {
      if (severity === "critical") return "text-red-600";
      if (severity === "warning") return "text-yellow-500";
      return "text-blue-500";
    },
    severityBarClass(severity) {
      if (severity === "critical") return "bg-red-400";
      if (severity === "warning") return "bg-yellow-400";
      return "bg-blue-400";
    },
    categoryIconClass(category) {
      if (category === "queue_performance")
        return "bg-violet-100 text-violet-600";
      if (category === "peak_hours") return "bg-orange-100 text-orange-600";
      if (category === "volume") return "bg-teal-100 text-teal-600";
      return "bg-blue-100 text-blue-600";
    },
  },

  mounted() {
    this.fetchAlerts();
    this.fetchSettings();
    this._pollInterval = setInterval(() => this.fetchAlerts(), 300000);
  },

  beforeUnmount() {
    clearInterval(this._pollInterval);
  },
};
</script>
